// The project page's Matters block (migration 0061): the project's named
// sub-groups of work — one short job each under a longer engagement — with
// what each still has unbilled and a "New invoice" that opens the builder
// limited to it. A matter is only a label; rates and the bill-to stay with the
// project.

import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { ProjectMatter } from '@stfors/shared-types';
import { useMatters, useUpdateMatter, useDeleteMatter } from '../api/queries/matters';
import { MatterFormModal } from './MatterFormModal';
import { IconAction } from './IconAction';
import { Toggle } from './Toggle';
import { can } from '../auth/store';
import { apiErrorText } from '../utils/apiErrors';

function inUse(m: ProjectMatter): number {
  return (m.time_entry_count ?? 0) + (m.expense_count ?? 0) + (m.invoice_count ?? 0);
}

function unbilledText(m: ProjectMatter): string {
  const t = m.unbilled_time_count ?? 0;
  const e = m.unbilled_expense_count ?? 0;
  if (t + e === 0) return 'Nothing unbilled';
  return [
    t ? `${t} time ${t === 1 ? 'entry' : 'entries'}` : null,
    e ? `${e} expense${e === 1 ? '' : 's'}` : null,
  ].filter(Boolean).join(' · ') + ' unbilled';
}

export function ProjectMattersSection({ projectId }: { projectId: number }): JSX.Element {
  const q = useMatters(projectId);
  const update = useUpdateMatter();
  const remove = useDeleteMatter();
  const canWrite = can('project.write');
  const [editing, setEditing] = useState<ProjectMatter | 'new' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const matters = q.data?.matters ?? [];
  const busy = update.isPending || remove.isPending;

  const setClosed = async (m: ProjectMatter, closed: boolean) => {
    setError(null);
    try {
      await update.mutateAsync({ id: m.id, closed });
    } catch (err) {
      setError(apiErrorText(err, 'Could not update the matter.'));
    }
  };

  const onDelete = async (m: ProjectMatter) => {
    if (!confirm(`Delete the matter "${m.name}"?`)) return;
    setError(null);
    try {
      await remove.mutateAsync(m.id);
    } catch (err) {
      setError(apiErrorText(err, 'Could not delete the matter.'));
    }
  };

  return (
    <section className="pd-block">
      <header className="pd-block-header">
        <h3>Matters</h3>
        <span className="pd-block-meta">
          {matters.length === 0
            ? 'Group short jobs under this project so each can be billed on its own'
            : `${matters.length} ${matters.length === 1 ? 'matter' : 'matters'}`}
        </span>
        {canWrite && (
          <button type="button" className="btn-charcoal-secondary pd-edit-btn" onClick={() => setEditing('new')}>
            Add matter
          </button>
        )}
      </header>

      {error && <p className="pd-error" role="alert">{error}</p>}

      {q.isPending ? (
        <p className="pd-block-empty">Loading…</p>
      ) : matters.length > 0 && (
        <table className="pd-table">
          <thead>
            <tr>
              <th>Matter</th>
              <th>Unbilled</th>
              <th>Open</th>
              <th aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {matters.map((m) => {
              const used = inUse(m);
              const hasUnbilled = (m.unbilled_time_count ?? 0) + (m.unbilled_expense_count ?? 0) > 0;
              return (
                <tr key={m.id} className={m.closed_at !== null ? 'pd-row-muted' : undefined}>
                  <td>
                    {m.name}
                    {m.description && <div className="pd-sub">{m.description}</div>}
                  </td>
                  <td>{unbilledText(m)}</td>
                  <td>
                    <Toggle
                      size="sm"
                      hideLabel
                      label={`${m.name} is open`}
                      title={m.closed_at === null
                        ? 'Open — offered when logging time and expenses'
                        : 'Closed — hidden from the pickers; what is on it can still be billed'}
                      checked={m.closed_at === null}
                      onChange={(open) => void setClosed(m, !open)}
                      disabled={!canWrite || busy}
                    />
                  </td>
                  <td className="pd-actions">
                    {can('invoice.write') && hasUnbilled && (
                      <Link
                        to={`/invoices/new?project_id=${projectId}&matter_id=${m.id}`}
                        className="btn-cyan-primary pd-edit-btn"
                      >
                        New invoice
                      </Link>
                    )}
                    {canWrite && (
                      <>
                        <IconAction
                          action="edit"
                          iconSize={14}
                          label={`Rename ${m.name}`}
                          onClick={() => setEditing(m)}
                          disabled={busy}
                        />
                        <IconAction
                          action="delete"
                          iconSize={14}
                          label={used > 0
                            ? 'In use — move its time, expenses and invoices off it first, or close it'
                            : `Delete ${m.name}`}
                          onClick={() => void onDelete(m)}
                          disabled={busy || used > 0}
                        />
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {editing !== null && (
        <MatterFormModal
          projectId={projectId}
          {...(editing === 'new' ? {} : { matter: editing })}
          onClose={() => setEditing(null)}
        />
      )}
    </section>
  );
}
